import { type ChatRequestOptions, CoreMessage } from 'ai'
import {ObjectId} from 'mongodb';
import {NotificationTypes, User} from "@/lib/notifications/notification-types";
import { Stripe } from 'stripe'
import { DemographicInference } from './matching/matching-types';
import { LanguageModelV1Source } from '@ai-sdk/provider'
import {AgentExecution} from "@/lib/types/mongo/schema/agent-execution";

export type ChatImage = {
    feedItemImage: any
    chatImage: any
    iconImage: any
}

export interface CountryType {
    name: string;
    code: string;
}

export interface StateType {
    name: string;
    code: string;
}

export enum ChatEventTypes {
    externalLinkClick = 'EXTERNAL_LINK_CLICK',
    addUserFromRecommendation = 'ADD_USER_FROM_RECOMMENDATION'
}

export interface ExternalLinkClickEvent {
    type: ChatEventTypes
    userId: string
    linkUrl: string
    linkTitle: string
    chatId: string
    messageId: string
}

export interface AddUserFromRecommendationEvent {
    type: ChatEventTypes
    inviteeId: string
    inviterId: string
    chatId: string
    messageId: string
}


export interface LinkItem {
    score: number
    title: string
    id: string
    url: string
    publishedDate: string
    author: string
    normalizedScore?: number
    websiteName: string
}

export type Message = CoreMessage & {
    id?: string
    chatId: string
    content: string
    geniiusData?: GeniiusData
    role: string
    model?: string
    provider?: string
    userInfo: any
    userId?: string
    imageUrl: string
    deletedAt?: Date;
    editedAt?: Date;
    initialUpvotes?: number
    initialDownvotes?: number
    userHasUpvoted?: boolean
    userHasDownvoted?: boolean
    initialComments?: number
    _id?: string | ObjectId
    createdAt: Date
    savedToDb?: boolean
    experimental_attachments?: {
        contentType: string;
        name: string;
        url: string;
    }[] | undefined;
    comments?: Comment[]
    systemMessageTypeId?: string
    embedding?: number[];
    tokensCount?: number
    relatedLinks?: LinkItem[]
    relatedLinksSearchQuery?: string
    showUserName?: boolean
    showTimestamp?:boolean
    sources?: LanguageModelV1Source[]
    reasoning?: string;
}

export enum ImageType {
    FeedItem = 'feedItem',
    PublicChat = 'publicChat',
    Full = 'full'
}
export interface PermittedUser {
    _id: string;
    memberType: 'admin' | 'participant' | 'moderator';
}

export interface Participant {
    _id: string;
    firstName: string;
    lastName: string;
    imageUrl: string;
    fullName?: string;
    username?:string
    headline?: string
    requestedAt?: Date
    isPending?: boolean;
    verified?: string | null;
}

export interface InvitedUser {
    inviteeId: string;
    inviteeName?: string;
    imageUrl?: string;
    headline?: string;
    createdAt?: string;
    status: 'pending' | 'accepted' | 'rejected'
    username: string
}


export interface Chat extends Record<string, any> {
    _id: ObjectId | string;
    title: string;
    createdAt: Date;
    updatedAt?: Date;
    model: string;
    userId: string | ObjectId;
    imageUrl: string;
    path: string;
    isPublic: boolean;
    publicPath?: string;
    tokensCount?: number;
    permittedUsers?: any;
    invitedUsers?: InvitedUser[];
    hasImage?: boolean;
    imageVersion?: number
    initialComments?: number;
    userInfo?: UserInfo
    editedAt?: Date;
    deletedAt?: Date;
    permittedUsersDetails?: Participant[];
    pendingJoinRequests?:  Participant[];
    isPinned?: boolean;
    isUnread?: boolean;
    sourcesPrivacy?: SourcesPrivacy;
    OGMetadata?: OGMetadata
    agentExecution?: AgentExecution
    chatPreviewLastMessage?: {
        _id: string;
        content: string | {type: string, text: string};
        createdAt: Date;
        userInfo: {
          _id: string;
          fullName: string;
          username: string;
        }
      }
      chatPreviewLastUserMessage?: {
        _id: string;
        content: string;
        createdAt: Date;
        userInfo: {
          _id: string;
          fullName: string;
          username: string;
        }
      }
}

// Serializable version of Chat for client new-chatbot-components
export type SerializableChat = Omit<Chat, 'createdAt' | 'updatedAt' | 'editedAt' | 'deletedAt' | 'chatPreviewLastMessage' | 'chatPreviewLastUserMessage'> & {
    createdAt: string;
    updatedAt?: string;
    editedAt?: string;
    deletedAt?: string;
    chatPreviewLastMessage?: Omit<Chat['chatPreviewLastMessage'], 'createdAt'> & { createdAt: string };
    chatPreviewLastUserMessage?: Omit<Chat['chatPreviewLastUserMessage'], 'createdAt'> & { createdAt: string };
}

export type ServerActionResult<Result> = Promise<
    | Result
    | {
    error: string
}
>

// Define an interface for the details object
export interface GeniiusData {
    keywords: string[];
    industry: string;
    topic: string;
    experts: string[];
    skills: string[];
    searchQuery?: string;
    embedding?: number[];
}

export interface ExtendedGeniiusData extends GeniiusData {
    userLocation?: {
        country?: string;
        state?: string;
        city?: string;
    };
    embedding?: number[]; // Add this line
}

export interface UserRecommendation {
    _id: string | ObjectId;
    firstName: string;
    lastName: string;
    imageUrl: string;
    username?: string;
    verified?: string | null;
    bio?: AuthorBio;
    settings?: UserSettings;
    autoJoinChat?: boolean;
    score: number;
    normalizedScore?: number;
    relevanceScore?: number;
    explanation?: string;
    locationMatch: boolean;
    skills?: string[];
    notificationSettings?: {
        [key: string]: boolean;
    };
    highlights?: Array<{
        path: string;
        texts: Array<{
            value: string;
            type: 'hit' | 'text';
        }>;
    }>;
}

export type Location = {
    country: string,
    state?: string,
    city?: string,
}

export type Geolocation = {
    latitude: number,
    longitude: number,
    userId: string;
}

export interface AuthorBio {
    aboutMe?: string;
    headline?: string;
    location?: {
      country?: string;
      state?: string;
      city?: string;
    };
    links?: string[];
    languages?: string[];
    school?: string;
    employer?: string;
    interests?: string[];
    placesKnownWell?: string[];
  }


export interface Comment {
    insertedId?: string;
    _id: string;
    chatId: string;
    messageId?: string; // Optional: To link a comment to a specific message
    _userId?: string;
    userId: string;
    userFullName: string;
    username: string;
    userPhoto: string;
    content: string;
    createdAt: Date;
    updatedAt?: Date; // Optional: To track edits
    editedAt?: Date; // Optional: To track edits
    upvotes?: number; // Number of upvotes for the comment
    userHasUpvoted?: boolean; // Indicates if the current user has upvoted this comment
    tagLine?: string; // Optional: User tagline
    parentCommentId?: string; // Optional: To link sub-comments to a parent comment
    deletedAt?: Date; // when comment has been deleted
    parentContent?: string; // Optional: Content of the parent comment
    parentUserId?: string; // Optional: User ID of the parent comment
    verifiedUser?: string | null;
    reactions?:  Reaction[];
    attachments?: {
        contentType: string;
        name: string;
        url: string;
    }[];
}

export interface Mention {
    userId: string;
    userName: string;
}

export interface CommentInput {
    chatId: string;
    userId: string;
    content: string;
    relatedMessageId?: string;
}

export enum GeniiusUserFields {
    _Id = "_id",
    ClerkUserId = "clerkUserId",
    FullName = "fullName",
    Username = "username",
    CreatedAt = "createdAt",
    ImageUrl = "imageUrl",
    EmailAddress = "emailAddress",
    LastActiveAt = "lastActiveAt",
    ExternalAccounts = "externalAccounts",
    AllowedGeneratedImages = "allowedGeneratedImages",
    Bio = "bio",
    NotificationSettings = "notificationSettings",
    Plan = "plan",
}

export interface UserCustomFilter {
    filter?: 'latest' | 'following' | 'nearMe' | 'community';
    sortBy?: 'latest' | 'upvotes' | 'views';
    userLocation?: {
        country: string;
        state?: string;
        city?: string;
    };
    organizationId?: string;
    userOrganizations?: Array<{ id: string }>;
}

export interface UserSettings {
    autoJoinChat: boolean;
    autoApprovePublishChat: boolean;
    isPublicProfile: boolean
    enabledApps: {
        [key: string]: boolean;
    };
}

export interface GeniiusUser {
    _id: string;
    clerkUserId: string;
    firstName: string;
    lastName: string;
    fullName: string;
    username: string;
    createdAt: Date;
    imageUrl: string;
    emailAddress: string;
    lastActiveAt: Date;
    externalAccounts: any;
    allowedGeneratedImages?: number
    bio?: AuthorBio
    notificationSettings?: {
        [K in keyof typeof NotificationTypes]: {
            enabled: boolean;
            immediate: boolean;
            dailyDigest: boolean;
            weeklyDigest: boolean;
        };
    };
    plan?: any, // TODO create type
    following?: any, // TODO create type
    followers?: any, // TODO create type
    profileViewsCount?: string | number
    geolocation: any
    stripe_customer_id?: string
    subscription_id?: string
    is_active: boolean
    is_admin?: boolean
    product_id?: string
    hasThemeProfileImage?: boolean,
    ThemeProfileImageVersion: number
    autoJoinChat?: boolean
    settings: UserSettings
    subscriptionDetails?: any
    embedding?: number[]; // Add this line
    verified?: string | null
    verificationRequestStatus?: string
    followedTopics: {
        name: string;
        subtopics: string[];
    }[];
    aiUsage: {count: number, date: string}
    referredBy?: string;
    organizations?: {
        id: string;
        name: string;
        role: string;
        slug: string;
        created_at: number;
        image_url: string;
        logo_url: string;
    }[];
    demographics?: DemographicInference;
    preferences?: {
        interestedIn?: 'male' | 'female' | 'all';
    };
    organizationMembers?: Partial<GeniiusUser>[]
    referredUsers?: Partial<GeniiusUser>[]
    OGMetadata?: OGMetadata
}

export interface UserSubscriptionDetails {
    is_active: boolean;
    subscription_id?: string | Stripe. Subscription | null;
    product_id?: string | Stripe. Product | Stripe. DeletedProduct | undefined;
    createdAt: Date;
    team_id?: string;
    is_admin?: boolean;
    removed_at?: Date;
}

export type GetMessageType = {
    notifier: Partial<User>,
    content?: string,
    parentContent?: string
    chatTitle?: string | null
    contentLink?: string
    messageContent?: string | null
    mentionType?: string
    isPublished?: boolean
}

export type Path = 'private' | 'public'


export type GetEmailContentType = {
    notifier: Partial<User>,
    content?: string,
    parentContent?: string
    chatTitle?: string
    contentLink: string
    notificationType?: any
    messageContent?: string | null
    mentionType?: string
    isPublished?: boolean
    notificationId?: string
    createdAt: Date
}

export const NotificationsCodeMap = {
    chat: 1,
    message: 2,
    profile: 3,
    comment: 4,
}

export interface NotificationItem {
    code: number,
    name: string,
    id: string | ObjectId
}

export interface UserInfo {
    _id: ObjectId | string;
    clerkUserId?: string;
    firstName: string | null;
    lastName: string | null;
    fullName: string | null;
    username: string | null;
    hasImage: boolean;
    imageUrl: string;
    isInvited?: boolean;
    headline?: string;
    location?: string[];
    sessionLastActiveAt?: string;
    bio?: any;
    autoJoinChat?: boolean
    settings?: UserSettings
    verified?: string
    organizationLogos?: string[]
    organizations?: Organization[]
    emailAddress?: string
    subscriptionDetails?: SubscriptionDetails
    organizationName?: string
}

export type SubscriptionDetails = {
    is_active: boolean;
    subscription_id?: string | Stripe. Subscription | null;
    product_id?: string | Stripe. Product | Stripe. DeletedProduct | undefined;
    createdAt: Date;
    team_id?: string;
    is_admin?: boolean;
    removed_at?: Date;
}

export interface Invitation {
    _id: ObjectId;
    chatId: string;
    inviterId: string;
    inviteeId: string;
    inviteeName: string;
    inviteePhoto: string;
    createdAt: Date;
    status: 'pending' | 'accepted' | 'rejected';
}


export function getClerkUserInfoToMessageUserInfo(user: any): UserInfo {
    if (!user) {
        console.log('user.id is not defined', user)
        //console.log('getClerkUserInfoToMessageUserInfo - user:', user);
        return {
            _id: '',
            clerkUserId: '',
            firstName: '',
            lastName: '',
            fullName: '',
            username: '',
            hasImage: false,
            imageUrl: '',
            isInvited: false,
            headline: '',
        };
    }
    return {
        _id: user._id,
        clerkUserId: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        fullName: user.fullName,
        username: user.username,
        hasImage: user.hasImage,
        imageUrl: user.imageUrl,
        isInvited: user.isInvited,
        headline: user.headline,
    };
}


export enum NewsFeedFilter {
    All = 'all',
    FOLLOWING = 'following',
    NEAR_ME = 'nearMe'
}

export interface LinkedInSearchResult {
    success: boolean;
    data?: {
      status?: string;
      message?: string;
      results?: any[];
    } | any[];
    error?: string;
  }

export type SocialSearchResult = {
    title: string | null;
    url: string | null;
    description: string | null;
    profilePicUrl?: string;
  };

export type ExampleMessage = {
  _id: string
  heading: string
  subheading: string
  message: string
}

export interface PersonalizedExample extends ExampleMessage {
  _id: string
  userId: ObjectId | string
  relatedUsers: UserRecommendation[]
  relevanceScore: number
  clicked: boolean
  clickedAt?: Date
  createdAt: Date
  bio?: AuthorBio
  firstName?: string
  lastName?: string
  imageUrl?: string
  combined_score?: number
}

export type PersonalizedExampleLog = {
  userId: string | ObjectId
  exampleId: string
  clicked: boolean
  clickedAt?: Date
  relatedUsers: string[]
  score: number
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  created_at: string;
  image_url?: string;
  logo_url?: string;
  isPublic?: boolean;
  expiration_date?: Date
}

export type Community = Organization;



export type ModerationType = 'comment' | 'message' | 'profile' | 'chat'

export interface ChatPermission {
  canEditTitle: boolean
  canEditRules: boolean
  canEditImage: boolean
  canManageUsers: boolean
  canDeleteChat: boolean
  canChangeVisibility: boolean
  // ... any other existing permissions
}

export interface TeamMember {
  role: 'admin' | 'member'
  status: 'active' | 'pending'
  joinedAt: Date,
  firstName: string;
  lastName: string;
  clerkUserId: string;
  autoJoinChat: { $ifNull: any[] };
  fullName: string
  imageUrl: string;
  username: string;
  headline: string | { $ifNull: string[] };
  hasImage: string | { $cond: any[] };
  autoApprovePublishChat: { $ifNull: any[] };
  _id?: string | number;
  verified: { $ifNull: any[] };
  organizationLogos: { $map: any };
  subscription_details: any;
  invitationStatus: 'pending | accepted';
  invitationCreatedAt: string;
}

export interface OGMetadata {
  ogImageUrl?: string
  ogGeneratedAt?: Date
  ogVersion?: number // For tracking regeneration needs
}

export interface SearchMetrics {
  searchTime: number;
  renderTime: number;
  totalTime: number;
  resultCount: number;
}

export interface VerificationRequest {
  userId: string
  reason?: string
  publicProfiles: string[]
  additionalInfo?: string
  fileData: string
}

export interface FeedbackRequest {
    userId: string
    feedback: string
    rating: number
    attachments?: string[]
}

export interface ReVerificationResponse {
    userId: string
    previousName: string
    previousLastName: string
}

export interface ReportRequest {
    reporterId: string
    targetId: string
    targetType: 'message' | 'comment' | 'user'
    reason: string
    details?: string
    content?: string
}

export interface SecondaryDataFetchOptions {
    fetchFollowData?: boolean;
    fetchProfileViews?: boolean;
    fetchVerificationStatus?: boolean;
    fetchAiUsage?: boolean;
    fetchOrganizationMembers?: boolean;
    fetchReferralData?: boolean;
}

export enum membershipRole {
    member = 'org:member',
    admin = "org:admin"
}

export interface SourcesPrivacy {
    hasPrivateSources: string[];
    organizationSources: string[];
}

export interface KnowledgeBaseDocument {
    content: string;
    metadata: Record<string, any>;
    searchScore?: number;  // Optional search score for ranking results
}


export interface SourceFileInMessage {
    fileName: string;
    fileNameInS3Bucket: string,
    selectedSpace: string,
    userId: string | ObjectId
}

export interface RelevantPerson {
    _id: string | ObjectId;
    bio: any;
    firstName: string;
    lastName: string;
    imageUrl: string;
    username?: string;
    settings: UserSettings,
    autoJoinChat: boolean
    score: number
    headline: string
}

export interface ExtendedChatRequestOptions extends ChatRequestOptions {
    options?: {
        messageType: 'peer' | 'ai'
    }
}

export interface Reaction {
    _id: string
    itemId: string
    userId: string
    fullName: string
    imageUrl: string
    createdAt: Date
    reaction: string
    deletedAt?: Date
}
